import React, { useState } from "react";
import styled from "styled-components";
import { useLocation, useHistory, useParams } from "react-router-dom";
import renderIf from "render-if";
import CircularProgress from "@material-ui/core/CircularProgress";

import Page from "../layout/Page";
import ContactForm from "../components/ContactForm";
import submitLead from "../data/submit-lead";
import useYMMTLabels from "../hooks/useYMMTLabels";
import useAccountSettings from "../hooks/useAccountSettings";
import formatVehicleTitle from "../utils/format-vehicle-title";
import { isOptInDisclaimer } from "../components/DisclaimerField";
import PageHeader from "../components/PageHeader";
import useTradeInState from "../hooks/useTradeInState";
import useForms from "../hooks/useForms";

import { vehicle } from "../data/vehicle";
import {
  fireFormSubmitEvent,
  fireFormInitiationEvent,
} from "../utils/gtm-events";

const ContactFormStyled = styled(ContactForm)`
  margin: 10px auto;
  max-width: 700px;
  padding: 10px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 350px;
  justify-content: center;
  align-items: center;
`;

const ContactPage = ({ children }) => {
  const params = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState();
  const [tradeIn, update] = useTradeInState();
  const location = useLocation();
  const { accountId } = params;

  const [
    {
      customDisclaimer,
      accountName,
      humanVerificationRequired,
      removeBranding,
      vehicleOfInterestRequired,
    },
  ] = useAccountSettings({ accountId });
  const [[year, make, model, trim]] = useYMMTLabels(params);
  const form = useForms(accountId);
  const vehicleTitle =
    year && make && model ? formatVehicleTitle({ year, make, model }) : "car";

  const actionName = "Get My Trade In Report Now!";

  const handleOnSubmit = async (values, actions) => {
    fireFormSubmitEvent({ leadId: tradeIn.leadId, year, make, model, trim });
    const { disclaimer, ...info } = values;
    const { setFieldError } = actions;

    setLoading(true);
    const contact = {
      ...info,
      acceptedDisclaimer: isOptInDisclaimer(customDisclaimer)
        ? disclaimer
        : undefined,
    };
    let interestedVehicle;
    try {
      // Only submit vehicle of interest if the dealer enabled the interested vehicle page in CRM.
      // While we do auto detect interested VIN we shouldn't send them through the lead without the user confirming
      // the YMMT and the dealer opting in for the interested vehicle feature.
      if (vehicleOfInterestRequired || form.showInterestedVehicle) {
        interestedVehicle = await vehicle(tradeIn.interestedVehicle);
        console.log("interested vehicle", interestedVehicle);
      }
    } catch (err) {
      console.log(err);
    }
    const tradeVehicle = await vehicle({ ...params, ...tradeIn });

    let initialSearch = location.search;
    const initialSearchParams = new URLSearchParams(initialSearch);
    initialSearchParams.delete("v");
    initialSearchParams.delete("d");
    initialSearch = initialSearchParams.toString();

    if (!initialSearch || initialSearch.length === 0) initialSearch = "?";
    if (initialSearch.length > 0 && !initialSearch.includes("?"))
      initialSearch = `?${initialSearch}`;

    const finalPathnameAndSearch = `${location.pathname.replace(
      "contact",
      "report"
    )}${initialSearch}&v=${tradeVehicle.tradeInRanges}&d=${Date.now()}`;

    const lead = {
      leadId: tradeIn.leadId,
      contact,
      tradeVehicle,
      interestedVehicle,
      account: {
        name: accountName,
        id: accountId,
      },
      actionName,
      leadUrl: `https://clients.purecars.com/trade/ui${finalPathnameAndSearch}`,
    };
    try {
      await submitLead(lead);
    } catch (err) {
      console.log(err);

      if (Array.isArray(err) && err[0].fieldName) {
        err.forEach((e) => setFieldError(e.fieldName, e.message));
        setLoading(false);
        return;
      }
    }

    update("date", Date.now());
    update("tradeInValues", tradeVehicle.tradeInRanges);

    history.push(finalPathnameAndSearch);

    setLoading(false);
  };

  const header = (
    <PageHeader
      title={`We calculated the trade in value for your ${vehicleTitle}`}
      subtitle="Please enter your information below and instantly see your free trade
      report!"
      link={`/${accountId}`}
      removeBranding={removeBranding}
    />
  );

  const terms = `${customDisclaimer} ${form.terms ? form.terms : ""}`.trim();

  return (
    <Page
      header={header}
      footer={children}
      onClick={() => {
        fireFormInitiationEvent({
          leadId: tradeIn.leadId,
          year,
          make,
          model,
          trim,
        });
      }}
    >
      {renderIf(!form.loading)(
        <ContactFormStyled
          onSubmit={handleOnSubmit}
          disclaimer={terms}
          required
          humanVerification={humanVerificationRequired}
          actionName={actionName}
          disabled={loading}
          preferredMethod={form.showPreferredMethodOfContact}
          message={form.showMessage}
        />
      )}
      {renderIf(form.loading)(
        <LoadingWrapper>
          <CircularProgress size={60} />
        </LoadingWrapper>
      )}
    </Page>
  );
};

export default ContactPage;
